/**
 * Dice Roll Types
 * Type definitions for the dice roll game plugin
 */

/**
 * Legend slot for displaying dice total outcomes
 */
export interface DiceRollLegendSlot {
  /** Dice total (2-12) */
  diceTotal: number;
  
  /** Associated prize ID */
  prizeId?: string;
  
  /** Prize index in campaign items array */
  prizeIndex?: number;
  
  /** Display name */
  prizeName?: string;
  
  /** Prize value/description */
  prizeValue?: string;
  
  /** Prize icon URL */
  prizeIcon?: string;
  
  /** Background color */
  color: string;
  
  /** Statistical probability of this dice total (0-1) */
  probability: number;
}

/**
 * Surface orientation mode (for the felt table surface)
 */
export type DiceRollSurfaceOrientation = 'flat' | 'vertical';

/**
 * Legend layout orientation
 */
export type DiceRollLegendOrientation = 'vertical' | 'horizontal';

/**
 * Surface size presets (for the table/felt surface)
 */
export type DiceRollSurfaceSize = 'small' | 'medium' | 'large';

/**
 * Dice total probability distribution (for 2d6)
 */
export const DICE_ROLL_PROBABILITIES: Record<number, number> = {
  2: 1/36,   // 2.78% (1 way: 1+1)
  3: 2/36,   // 5.56% (2 ways)
  4: 3/36,   // 8.33% (3 ways)
  5: 4/36,   // 11.11% (4 ways)
  6: 5/36,   // 13.89% (5 ways)
  7: 6/36,   // 16.67% (6 ways - most common)
  8: 5/36,   // 13.89% (5 ways)
  9: 4/36,   // 11.11% (4 ways)
  10: 3/36,  // 8.33% (3 ways)
  11: 2/36,  // 5.56% (2 ways)
  12: 1/36   // 2.78% (1 way: 6+6)
} as const;

/**
 * Physics constants for dice roller configuration
 */
export const DICE_ROLL_PHYSICS = {
  /** Base gravity for medium surface size (scales proportionally with surface for consistent fall times) */
  BASE_GRAVITY: 50,
  
  /** Die start height multiplier (units above floor surface) */
  DIE_START_HEIGHT_MULTIPLIER: 2,
} as const;

/**
 * Dice roll game settings (matches Campaign.experience.settings for type='dice-roll')
 */
export interface DiceRollSettings {
  type: 'dice-roll';
  
  /**
   * Surface size preset (for the table/felt)
   * - 'small': 750px (75% scale)
   * - 'medium': 1000px (baseline, 100% scale)
   * - 'large': 1250px (125% scale)
   */
  surfaceSize?: DiceRollSurfaceSize;
  
  /**
   * Surface orientation mode
   * - 'flat': Surface lies horizontal (for flat markers/surfaces)
   * - 'vertical': Surface perpendicular to marker (for vertical signs)
   */
  surfaceOrientation?: DiceRollSurfaceOrientation;
  
  /**
   * Legend layout orientation
   * - 'vertical': Stacked list (mobile-friendly, default)
   * - 'horizontal': Side-by-side strip
   */
  legendOrientation?: DiceRollLegendOrientation;
  
  /**
   * Prize-to-dice-total mapping (optional manual override)
   * Key: dice total (2-12), Value: prize index
   * If not provided, auto-assignment algorithm will be used
   */
  prizeMapping?: Record<number, number>;
  
  /**
   * Default prize colors (11 colors for dice totals 2-12)
   */
  prizeColors?: string[];
  
  /**
   * Center logo URL (displayed in middle of table surface)
   */
  logoUrl?: string;
  
  /**
   * Surface/table color (hex or CSS color)
   * Default: '#654321' (rich brown wood)
   */
  surfaceColor?: string;
  
  /**
   * Surface roughness (0-1)
   * Controls surface finish: 0 = glossy, 1 = matte
   * Default: 0.9 (matte wood)
   */
  surfaceRoughness?: number;
  
  /**
   * Surface metalness (0-1)
   * Controls metallic appearance: 0 = non-metal, 1 = pure metal
   * Default: 0.1 (wood has minimal metallic properties)
   */
  surfaceMetalness?: number;
  
  /**
   * Surface depth/thickness (3D units)
   * Default: 0.2
   */
  surfaceDepth?: number;
  
  /**
   * Enable 3D shadows
   */
  castShadow?: boolean;
  
  /**
   * Enable orbit controls (for debugging/testing)
   */
  enableCameraControls?: boolean;
  
  // Preview mode flag (added by Portal)
  _previewPrizes?: any[];
}

/**
 * Game state for tracking roll progress (simplified for single-roll gameplay)
 */
export interface DiceRollState {
  /** Number of rolls performed */
  rollCount: number;
  
  /** Winning prize ID */
  winningPrizeId: string;
  
  /** Winning prize index in config.items array */
  winningPrizeIndex: number;
  
  /** Winning dice total (2-12) */
  winningDiceTotal: number;
  
  /** Current dice values [die1, die2] */
  currentDiceValues?: [number, number];
  
  /** Is dice roll animation in progress */
  isRolling: boolean;
  
  /** Is game complete */
  isComplete: boolean;
  
  /** Has player won (final state) */
  hasWon: boolean;
}

/**
 * Roll outcome from dice roller
 */
export interface DiceRollOutcome {
  /** Die 1 value (1-6) */
  die1: number;
  
  /** Die 2 value (1-6) */
  die2: number;
  
  /** Total of both dice (2-12) */
  total: number;
  
  /** Is this the winning roll */
  isWinningRoll: boolean;
}

/**
 * Default prize colors for legend slots
 */
export const DICE_ROLL_DEFAULT_PRIZE_COLORS = [
  '#F44336', // Red (highest value - dice total 12)
  '#FFD700', // Gold (2nd highest - dice total 2)
  '#FF9800', // Orange
  '#FFEB3B', // Yellow
  '#4CAF50', // Green
  '#00BCD4', // Cyan
  '#2196F3', // Blue
  '#9C27B0', // Purple
  '#E91E63', // Magenta
  '#795548', // Brown
  '#607D8B', // Blue Grey (lowest value - most common total)
];

/**
 * Logical unit dimensions for surface layout
 */
export const DICE_ROLL_SURFACE_SIZE_UNITS = 20; // Square surface is 20×20 units

// Pixels per unit for each size preset
export const DICE_ROLL_PIXELS_PER_UNIT: Record<DiceRollSurfaceSize, number> = {
  small: 37.5,   // 20 × 37.5 = 750px surface
  medium: 50,    // 20 × 50 = 1000px surface (baseline)
  large: 62.5    // 20 × 62.5 = 1250px surface
};

// Surface pixel sizes (clean values for easy scaling)
export const DICE_ROLL_SURFACE_SIZE_PIXELS: Record<DiceRollSurfaceSize, number> = {
  small: 750,   // 75% of medium
  medium: 1000, // Baseline
  large: 1250   // 125% of medium
};

/**
 * Auto-assign prizes to dice totals based on probability matching
 * 
 * Algorithm:
 * 1. Prize[0] (lowest probability) → Dice total 12 (2.78%)
 * 2. Prize[1] (2nd lowest) → Dice total 2 (2.78%)
 * 3. Sort remaining prizes by probability ascending
 * 4. Sort remaining dice totals by probability ascending
 * 5. Match prizes to dice totals sequentially
 * 6. If fewer than 11 prizes, backfill with highest probability prize (last in array)
 * 
 * @param prizes - Array of prizes sorted by probability (ascending = lowest first)
 * @param minPrizes - Minimum number of prizes required (default: 6)
 * @returns Mapping of dice total → prize index
 */
export function autoAssignPrizesToDiceTotals(
  prizes: Array<{ probability?: number }>,
  minPrizes = 6
): Record<number, number> {
  if (prizes.length < minPrizes) {
    throw new Error(`Dice Roll requires at least ${minPrizes} prizes`);
  }

  const mapping: Record<number, number> = {};
  
  // Sort prizes by probability (ascending = lowest first)
  const sortedPrizes = prizes
    .map((prize, index) => ({ index, probability: prize.probability || 0 }))
    .sort((a, b) => a.probability - b.probability);
  
  // Assign top prize (lowest probability) to dice total 12
  mapping[12] = sortedPrizes[0].index;
  
  // Assign 2nd prize to dice total 2
  if (prizes.length >= 2) {
    mapping[2] = sortedPrizes[1].index;
  } else {
    // Fallback: use same prize as total 12
    mapping[2] = sortedPrizes[0].index;
  }
  
  // Get remaining dice totals sorted by probability (ascending)
  const remainingTotals = [3, 4, 5, 6, 7, 8, 9, 10, 11].sort(
    (a, b) => DICE_ROLL_PROBABILITIES[a] - DICE_ROLL_PROBABILITIES[b]
  );
  
  // Get remaining prizes (skip first 2)
  const remainingPrizes = sortedPrizes.slice(2);
  
  // Backfill prize (highest probability = last in sorted array)
  const backfillPrizeIndex = sortedPrizes[sortedPrizes.length - 1].index;
  
  // Assign remaining prizes to dice totals
  remainingTotals.forEach((total, i) => {
    if (i < remainingPrizes.length) {
      mapping[total] = remainingPrizes[i].index;
    } else {
      // Backfill with highest probability prize
      mapping[total] = backfillPrizeIndex;
    }
  });
  
  return mapping;
}

/**
 * Get prize index for a specific dice total using auto-assignment or manual mapping
 * 
 * @param diceTotal - Dice total (2-12)
 * @param prizes - Array of prizes
 * @param prizeMapping - Optional manual mapping override
 * @returns Prize index, or undefined if no mapping exists
 */
export function getPrizeIndexForDiceTotal(
  diceTotal: number,
  prizes: Array<{ probability?: number }>,
  prizeMapping?: Record<number, number>
): number | undefined {
  // Use manual mapping if provided
  if (prizeMapping && prizeMapping[diceTotal] !== undefined) {
    return prizeMapping[diceTotal];
  }
  
  // Otherwise use auto-assignment
  const autoMapping = autoAssignPrizesToDiceTotals(prizes);
  return autoMapping[diceTotal];
}
